<template>
  <div class="spot-body">
    <!-- 功能二：列表数据（运营管理「收益列表」式：标题 + 筛选 + 表格） -->
    <div class="spot-list-panel">
      <div class="spot-list-header">
        <h3 class="spot-panel-title">列表数据</h3>
      </div>
      <div class="spot-filters-layout">
        <el-form :model="spotForm" label-position="top" class="spot-filter-form" @submit.prevent>
          <div class="spot-filter-grid">
            <el-form-item :label="$str.getKey('收益管理/地区')">
              <el-cascader
                v-model="spotForm.areaCode"
                class="spot-field-wide"
                :options="adOptions"
                :props="cascaderProps"
                :show-all-levels="false"
                clearable
                placeholder="请选择"
              />
            </el-form-item>
            <el-form-item :label="$str.getKey('收益管理/日期')">
              <el-date-picker
                v-model="spotForm.priceDate"
                class="spot-field-date"
                type="date"
                value-format="YYYY-MM-DD"
                :clearable="false"
              />
            </el-form-item>
            <el-form-item label="点数">
              <el-select v-model="spotForm.pointCount" class="spot-field-narrow" placeholder="点数">
                <el-option :value="96" label="96个点" />
                <el-option :value="24" label="24个点" />
              </el-select>
            </el-form-item>
            <el-form-item label="价格类型">
              <el-select v-model="spotForm.priceType" class="spot-field-narrow" clearable placeholder="请选择">
                <el-option :value="1" label="日前电价" />
                <el-option :value="2" label="实时电价" />
              </el-select>
            </el-form-item>
          </div>
        </el-form>
        <div class="spot-actions">
          <el-button class="meter-feature-btn meter-feature-btn--lg" type="primary" :loading="loading" :disabled="!spotForm.areaCode" @click="onQuery">查询</el-button>
          <el-button class="meter-feature-btn meter-feature-btn--lg" type="primary" :disabled="!spotForm.areaCode" @click="toggleEditAll">{{ editAll ? '取消编辑' : '编辑' }}</el-button>
          <el-button
            v-if="editAll"
            class="meter-feature-btn meter-feature-btn--lg"
            type="primary"
            :loading="saveLoading"
            :disabled="!spotForm.areaCode"
            @click="onSaveEdit"
          >
            保存
          </el-button>
          <!-- 导入必须落到具体地区，未选地区时置灰 -->
          <el-button
            class="meter-feature-btn meter-feature-btn--lg"
            type="primary"
            :disabled="!spotForm.areaCode"
            @click="openImportDialog"
          >
            导入
          </el-button>
          <!-- <el-button class="meter-feature-btn meter-feature-btn--lg" type="primary" :loading="exportLoading" :disabled="!spotForm.areaCode" @click="onExport">导出</el-button> -->
        </div>
      </div>

      <div class="spot-table-wrap" v-loading="loading">
        <div class="spot-table-inner">
          <!-- 与 business-operations/summary 收益列表一致：Vuetify v-table + gm-table / ops-table -->
          <v-table
            density="compact"
            fixed-header
            height="340"
            class="bg-transparent flex-grow-1 gm-table ops-table ops-spot-table"
            style="width: 100%"
          >
            <thead>
              <tr>
                <th class="spot-col-time" style="width: 88px">时刻</th>
                <th class="text-end" style="min-width: 132px">市场电价(元/兆瓦时)</th>
                <th class="text-end" style="min-width: 132px">服务费(元/兆瓦时)</th>
                <th class="text-end" style="min-width: 140px">最终执行价(元/兆瓦时)</th>
                <th class="text-center" style="width: 110px">数据来源</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!viewRows.length">
                <td colspan="5" class="spot-empty-cell">暂无数据，请先查询或导入现货电价</td>
              </tr>
              <tr v-for="(row, idx) in viewRows" :key="String(row.timeSlot) + '-' + idx">
                <td class="spot-col-time">{{ row.timeSlot }}</td>
                <td class="text-end">
                  <template v-if="editAll">
                    <el-input-number
                      v-model="row.marketPrice"
                      :controls="false"
                      :step="0.000001"
                      :precision="6"
                      class="cell-number"
                      @change="markDirty(row)"
                    />
                  </template>
                  <span v-else class="cell-text">{{ formatNumber(row.marketPrice) }}</span>
                </td>
                <td class="text-end">
                  <template v-if="editAll">
                    <el-input-number
                      v-model="row.servicePrice"
                      :controls="false"
                      :step="0.000001"
                      :precision="6"
                      class="cell-number"
                      @change="markDirty(row)"
                    />
                  </template>
                  <span v-else class="cell-text">{{ formatNumber(row.servicePrice) }}</span>
                </td>
                <td class="text-end">
                  <span class="cell-text">{{ formatNumber(row.finalPrice) }}</span>
                </td>
                <td class="text-center">
                  <span class="cell-text cell-text--center">{{ row.sourceType === 2 ? '人工调整' : '市场接口' }}</span>
                </td>
              </tr>
            </tbody>
          </v-table>
        </div>
      </div>
    </div>

    <div class="spot-chart">
      <div v-if="viewRows.length === 0" class="spot-chart-empty">暂无可展示数据（请调整筛选条件）</div>
      <VChart v-else class="spot-chart-inner" :option="spotLineOption" :key="chartKey" autoresize />
    </div>

    <!-- 数据导入弹框：下载模板 + 上传，地区取自上方筛选，弹框内只读展示 -->
    <el-dialog v-model="importDialogVisible" title="数据导入" width="480px" append-to-body>
      <div class="import-dialog">
        <div class="import-dialog__area">
          <span class="import-dialog__label">导入地区</span>
          <span class="import-dialog__value">{{ getSelectedLabel(spotForm.areaCode) || '-' }}</span>
        </div>
        <div class="import-dialog__step">
          <div class="import-dialog__step-text">
            <span class="import-dialog__step-title">1. 下载模板</span>
            <span class="import-dialog__step-hint">按模板表头填写电价数据</span>
          </div>
          <el-button type="primary" :loading="downloadLoading" @click="onDownloadTemplate">下载模板</el-button>
        </div>
        <div class="import-dialog__step">
          <div class="import-dialog__step-text">
            <span class="import-dialog__step-title">2. 导入数据</span>
            <span class="import-dialog__step-hint">支持 {{ acceptExcel }}，导入后自动刷新列表</span>
          </div>
          <el-upload
            :show-file-list="false"
            action="#"
            :http-request="onImport"
            :accept="acceptExcel"
            :disabled="importLoading || !spotForm.areaCode"
          >
            <el-button type="primary" :loading="importLoading" :disabled="!spotForm.areaCode">选择文件</el-button>
          </el-upload>
        </div>
      </div>
      <template #footer>
        <el-button @click="importDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import dayjs from 'dayjs'
import { ElLoading, ElMessageBox } from 'element-plus'
import VChart from 'vue-echarts'
import { useAppStore } from '@/store/modules/app'
import {
  addSpotPriceData,
  deleteSpotPriceData,
  exportSpotPriceData,
  getSpotPriceData,
  getSpotPriceTemplate,
  importSpotPriceTemplate,
} from '@/api/business-operations'

const $str = window.$str

const props = defineProps({
  adOptions: {
    type: Array,
    default: () => [],
  },
  cascaderProps: {
    type: Object,
    default: () => ({
      checkStrictly: true,
      expandTrigger: 'hover',
      emitPath: false,
    }),
  },
})

const spotForm = reactive({
  areaCode: '',
  priceDate: dayjs().format('YYYY-MM-DD'),
  pointCount: 96,
  priceType: 2,
})

const acceptExcel = '.xlsx,.xls'
const loading = ref(false)
const downloadLoading = ref(false)
const importLoading = ref(false)
const exportLoading = ref(false)
const saveLoading = ref(false)

const editAll = ref(false)
const editSnapshot = ref([])
const importDialogVisible = ref(false)

const openImportDialog = () => {
  if (!spotForm.areaCode) return
  importDialogVisible.value = true
}

const stationId = computed(() => useAppStore().stationId)

const rawRows = ref([])
const editableRows = ref([])

/**
 * 空白骨架：96 个点（15分钟一条），00:00 ~ 23:45。
 * 只在「查不到任何数据」时用，给手工从零录入提供可编辑的行；
 * 一旦有数据，一律以数据为准，绝不拿它去回填/对齐。
 */
const makeBlankSlots = () => {
  const out = []
  for (let minutes = 0; minutes < 24 * 60; minutes += 15) {
    const hh = String(Math.floor(minutes / 60)).padStart(2, '0')
    const mm = String(minutes % 60).padStart(2, '0')
    out.push({
      id: undefined,
      timeSlot: `${hh}:${mm}`,
      marketPrice: 0,
      servicePrice: 0,
      finalPrice: '0.000000',
      sourceType: 1,
      _dirty: false,
      _isNew: true,
      _deleting: false,
    })
  }
  return out
}

/**
 * 表格行：有数据就完全按数据渲染，没有数据才铺 96 点空白骨架。
 *
 * 关键是不再做「骨架 × 数据」的按时点回填——骨架把口径写死（曾是 00:15~24:00），
 * 与导入文件的实际时点（如 00:00~23:45）对不上时列表会整体错位，
 * 提交时也会把本地口径而不是真实数据发给后端。
 */
const buildTableRows = (rows = []) => {
  if (!rows || !rows.length) return makeBlankSlots()
  return rows.map((r) => ({
    ...r,
    marketPrice: Number.isFinite(Number(r.marketPrice)) ? Number(r.marketPrice) : 0,
    servicePrice: Number.isFinite(Number(r.servicePrice)) ? Number(r.servicePrice) : 0,
    _dirty: !!r._dirty,
    _isNew: false,
    _deleting: false,
  }))
}

const cloneRows = (rows = []) =>
  rows.map((r) => ({
    ...r,
    marketPrice: Number.isFinite(Number(r.marketPrice)) ? Number(r.marketPrice) : 0,
    servicePrice: Number.isFinite(Number(r.servicePrice)) ? Number(r.servicePrice) : 0,
  }))

const tableRows = computed(() => editableRows.value || [])

const toggleEditAll = () => {
  if (editAll.value) {
    editableRows.value = cloneRows(editSnapshot.value)
    editAll.value = false
    return
  }
  editSnapshot.value = cloneRows(tableRows.value)
  editAll.value = true
}

const isHourSlot = (t) => String(t || '').endsWith(':00')

/**
 * 表格/图表展示行：只按「点数」筛选（96 / 24 整点）。
 * 「价格类型(日前/实时)」仅通过 getSpotPriceData 的 priceType 参数拉数，不在此再用同一字段过滤——
 * 否则与行内「数据来源」语义（市场接口/人工调整 使用 sourceType）冲突，会导致筛选后 0 行、图表空白。
 */
const viewRows = computed(() => {
  let rows = tableRows.value || []
  if (Number(spotForm.pointCount) === 24) {
    rows = rows.filter((r) => isHourSlot(r.timeSlot))
  }
  return rows
})

const chartKey = computed(() => {
  return `${spotForm.priceDate || ''}-${spotForm.areaCode || ''}-${spotForm.pointCount || ''}-${spotForm.priceType || ''}-${editAll.value ? 'e' : 'r'}-${viewRows.value.length}`
})

const formatNumber = (v) => {
  const n = Number(v)
  if (!Number.isFinite(n)) return '-'
  return n.toFixed(6)
}

const markDirty = (row) => {
  if (!row) return
  const mp = Number(row.marketPrice)
  const sp = Number(row.servicePrice)
  const hasMp = Number.isFinite(mp)
  const hasSp = Number.isFinite(sp)
  row.finalPrice = hasMp || hasSp ? ((hasMp ? mp : 0) + (hasSp ? sp : 0)).toFixed(6) : null
  row._dirty = true
  row.sourceType = 2
}

const getSelectedLabel = (value) => {
  const dfs = (list) => {
    if (!Array.isArray(list)) return null
    for (const it of list) {
      if (String(it?.value) === String(value)) return it?.label || null
      const hit = dfs(it?.children)
      if (hit) return hit
    }
    return null
  }
  return dfs(props.adOptions) || ''
}

/** 列表查询所需条件是否已齐（无提示，用于导入成功后是否自动刷新） */
const listQueryReady = () => {
  return !!(stationId.value && spotForm.areaCode && spotForm.priceDate)
}

const ensureRequired = () => {
  if (!stationId.value) {
    window.dispatchNotify?.({ type: 'warning', message: '缺少电站ID，请先选择电站' })
    return false
  }
  if (!spotForm.areaCode) {
    window.dispatchNotify?.({ type: 'warning', message: '请选择地区' })
    return false
  }
  if (!spotForm.priceDate) {
    window.dispatchNotify?.({ type: 'warning', message: '请选择日期' })
    return false
  }
  return true
}

const onQuery = async () => {
  if (!ensureRequired()) return
  loading.value = true
  try {
    const res = await getSpotPriceData({
      stationId: Number(stationId.value),
      areaCode: spotForm.areaCode || undefined,
      priceDate: spotForm.priceDate,
      priceType: spotForm.priceType || undefined,
    })
    const list = Array.isArray(res?.data) ? res.data : []
    rawRows.value = list
      .map((it) => ({
        id: it.id,
        timeSlot: it.timeSlot,
        marketPrice: it.marketPrice ?? 0,
        servicePrice: it.servicePrice ?? 0,
        finalPrice: it.finalPrice ?? (Number.isFinite(Number(it.marketPrice)) || Number.isFinite(Number(it.servicePrice))
          ? (Number(it.marketPrice || 0) + Number(it.servicePrice || 0)).toFixed(6)
          : null),
        sourceType: it.sourceType ?? 1,
        _dirty: false,
        _isNew: false,
        _deleting: false,
      }))
      .sort((a, b) => String(a.timeSlot).localeCompare(String(b.timeSlot)))
    editableRows.value = buildTableRows(rawRows.value)
    if (!editAll.value) {
      editSnapshot.value = cloneRows(editableRows.value)
    }
  } catch (e) {
    rawRows.value = []
    editableRows.value = buildTableRows([])
    editSnapshot.value = cloneRows(editableRows.value)
  } finally {
    loading.value = false
  }
}

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}

const onDownloadTemplate = async () => {
  downloadLoading.value = true
  try {
    const blob = await getSpotPriceTemplate({})
    downloadBlob(blob, `现货电价导入模板.xlsx`)
  } catch (e) {
    // ignore, global request already toasts
  } finally {
    downloadLoading.value = false
  }
}

const onImport = async (options) => {
  const raw = options?.file?.raw ?? options?.file
  if (!raw) return
  if (!spotForm.areaCode) {
    window.dispatchNotify?.({ type: 'warning', message: '请先选择地区' })
    return
  }
  importLoading.value = true
  const loadingMask = ElLoading.service({
    lock: true,
    fullscreen: true,
    text: '正在导入，请稍候…',
    background: 'rgba(255, 255, 255, 0.82)',
  })
  try {
    const fd = new FormData()
    fd.append('file', raw)
    fd.append('stationId', '0')
    // 导入必须归属到具体地区，与筛选栏选中的地区保持一致
    fd.append('areaCode', String(spotForm.areaCode))
    fd.append('areaName', getSelectedLabel(spotForm.areaCode) || '')
    await importSpotPriceTemplate(fd)
    window.dispatchNotify?.({ type: 'success', message: '导入成功' })
    importDialogVisible.value = false
    if (listQueryReady()) {
      await onQuery()
    }
  } catch (e) {
    const msg =
      e?.response?.data?.msg ||
      e?.message ||
      '导入失败，请检查 Excel 是否按模板填写（地区/电站/日期/时点等必填）'
    window.dispatchNotify?.({ type: 'error', message: String(msg) })
  } finally {
    loadingMask.close()
    importLoading.value = false
  }
}

const onExport = async () => {
  if (!ensureRequired()) return
  exportLoading.value = true
  try {
    const blob = await exportSpotPriceData({
      stationId: Number(stationId.value),
      areaCode: spotForm.areaCode || undefined,
      priceDate: spotForm.priceDate,
    })
    const code = spotForm.areaCode ? `_${spotForm.areaCode}` : ''
    downloadBlob(blob, `现货电价_${spotForm.priceDate}${code}.xlsx`)
  } catch (e) {
    // ignore, global request already toasts
  } finally {
    exportLoading.value = false
  }
}

// 已取消逐行编辑/保存：仅整体编辑 + 抽屉底部“确定”批量下发

/**
 * 抽屉“确定”下发：批量保存（使用 addSpotPriceData）
 * - 下发的时点集合与列表当前数据完全一致，不再补齐成固定的 96 点
 */
const submitIssue = async () => {
  if (!ensureRequired()) return false
  const rows = tableRows.value || []
  if (!rows.length) {
    window.dispatchNotify?.({ type: 'warning', message: '暂无可下发的电价数据，请先查询或导入' })
    return false
  }
  const invalid = rows.find((r) => !Number.isFinite(Number(r.marketPrice)) || !Number.isFinite(Number(r.servicePrice)))
  if (invalid) {
    window.dispatchNotify?.({ type: 'warning', message: `请补全所有时点电价数据（缺失：${invalid.timeSlot}）` })
    return false
  }

  await addSpotPriceData({
    areaName: spotForm.areaCode ? getSelectedLabel(spotForm.areaCode) : undefined,
    areaCode: spotForm.areaCode || undefined,
    stationId:0,
    priceType: spotForm.priceType,
    priceDate: spotForm.priceDate,
    snapshots: rows.map((r) => ({
      timeSlot: r.timeSlot,
      marketPrice: Number(r.marketPrice),
      servicePrice: Number(r.servicePrice),
      sourceType: r.sourceType ?? 2,
    })),
  })
  window.dispatchNotify?.({ type: 'success', message: '下发成功' })
  await onQuery()
  return true
}

const onSaveEdit = async () => {
  if (!editAll.value) return
  saveLoading.value = true
  try {
    const ok = await submitIssue()
    if (ok) {
      editAll.value = false
      editSnapshot.value = cloneRows(tableRows.value)
    }
  } finally {
    saveLoading.value = false
  }
}

defineExpose({
  submitIssue,
})

const onDeleteRow = async (row) => {
  if (!row?.id) return
  try {
    await ElMessageBox.confirm('确认删除该时点电价数据？', '提示', { type: 'warning' })
  } catch {
    return
  }
  row._deleting = true
  try {
    await deleteSpotPriceData(row.id)
    window.dispatchNotify?.({ type: 'success', message: '删除成功' })
    await onQuery()
  } finally {
    row._deleting = false
  }
}

const spotLineOption = computed(() => {
  const rows = viewRows.value || []
  const points = rows.map((r) => {
    const fp = Number(r.finalPrice)
    if (Number.isFinite(fp)) return fp

    const mp = Number(r.marketPrice)
    const sp = Number(r.servicePrice)
    const hasMp = Number.isFinite(mp)
    const hasSp = Number.isFinite(sp)
    if (!hasMp && !hasSp) return null
    return (hasMp ? mp : 0) + (hasSp ? sp : 0)
  })
  return /** @type {import('echarts').EChartsOption} */ ({
    // 留出足够边距，避免 Y 轴名称与刻度、tooltip 在左上角互相遮挡
    grid: { left: 56, right: 28, top: 28, bottom: 52, containLabel: true },
    tooltip: {
      trigger: 'axis',
      confine: true,
      valueFormatter: (v) => (Number.isFinite(Number(v)) ? `${Number(v).toFixed(6)} 元/兆瓦时` : '-'),
    },
    xAxis: {
      type: 'category',
      data: rows.map((r) => r.timeSlot),
      axisLabel: { color: 'rgba(46,46,46,0.7)', interval: 11, margin: 10 },
    },
    yAxis: {
      type: 'value',
      name: '电价(元/兆瓦时)',
      nameLocation: 'middle',
      nameGap: 48,
      nameTextStyle: { color: 'rgba(46,46,46,0.55)', fontSize: 12, align: 'center' },
      splitLine: { lineStyle: { color: 'rgba(0,0,0,0.06)' } },
      axisLabel: { color: 'rgba(46,46,46,0.6)', margin: 10 },
      // 全 0 或全同值时，给 y 轴留边距，避免“看起来是空的”
      min: (ext) => {
        if (!ext || !Number.isFinite(ext.min) || !Number.isFinite(ext.max)) return undefined
        if (ext.min !== ext.max) return Math.max(0, ext.min)
        return Math.max(0, ext.min - 0.1)
      },
      max: (ext) => {
        if (!ext || !Number.isFinite(ext.min) || !Number.isFinite(ext.max)) return undefined
        if (ext.min !== ext.max) return ext.max
        return ext.max + 0.1
      },
    },
    series: [
      {
        name: '电价',
        type: 'line',
        data: points,
        showSymbol: false,
        smooth: true,
        lineStyle: { width: 2, color: '#00DD7A' },
        areaStyle: { opacity: 0 },
      },
    ],
  })
})

onMounted(() => {
  // onQuery()
})
</script>

<style scoped lang="scss">
@import '../../_shared/feature-button.scss';

.spot-body {
  width: 100%;
  flex: 1;
  min-height: 0;
  padding: 0 12px 8px;
  display: flex;
  flex-direction: column;
  gap: 22px;
}

/* —— 导入弹框 —— */
.import-dialog {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.import-dialog__area {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  background: rgba(58, 104, 255, 0.06);

  .import-dialog__label {
    font-size: 12px;
    color: rgba(46, 46, 46, 0.6);
  }

  .import-dialog__value {
    font-size: 14px;
    font-weight: 600;
    color: #2e2e2e;
  }
}

.import-dialog__step {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;

  .import-dialog__step-text {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .import-dialog__step-title {
    font-size: 14px;
    color: rgba(30, 30, 30, 0.92);
  }

  .import-dialog__step-hint {
    font-size: 12px;
    color: rgba(46, 46, 46, 0.52);
  }
}

/* —— 列表数据：对齐运营管理「收益列表」面板（毛玻璃底 + 边框 + 表头灰底） —— */
.spot-list-panel {
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 20px;
  padding: 14px 20px 18px;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(10px);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.spot-list-header {
  margin-bottom: 14px;
}

.spot-panel-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #2e2e2e;
}

.spot-filters-layout {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px 20px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.spot-filter-form {
  flex: 1;
  min-width: 0;
}

.spot-filter-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 4px 12px;
  width: 100%;
  align-items: end;
}

@media (max-width: 1200px) {
  .spot-filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .spot-filter-grid {
    grid-template-columns: 1fr;
  }
}

.spot-filter-form :deep(.el-form-item) {
  margin-bottom: 0;
}

.spot-filter-form :deep(.el-form-item__label) {
  font-size: 12px;
  color: rgba(46, 46, 46, 0.65);
  line-height: 1.3;
  margin-bottom: 6px;
  padding: 0;
}

.spot-field-wide {
  width: 100%;
}

.spot-field-date {
  width: 100%;
}

.spot-field-narrow {
  width: 100%;
}

.spot-actions {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  flex-shrink: 0;
  padding-bottom: 2px;
}

/* 表格容器：与 business-operations summary 中 ops-table-wrap / ops-revenue-table-inner 一致 */
.spot-table-wrap {
  margin-top: 4px;
  flex: 1;
  min-height: 0;
  border-radius: 8px;
  overflow: hidden;
}

.spot-table-inner {
  flex: 1;
  min-height: 0;
  max-width: 100%;
  overflow: auto;
  background: transparent;
}

/* 与 @/views/business-operations/summary/index.vue 中 .ops-revenue-table 一致 */
.ops-spot-table {
  font-size: 13px;
  flex: 1;
  min-height: 0;
  background: transparent !important;
}

.ops-spot-table :deep(.v-table__wrapper) {
  overflow-x: auto;
  background: transparent !important;
}

.ops-spot-table :deep(table) {
  border-collapse: collapse;
  width: 100%;
  table-layout: fixed;
  background: transparent;
}

.ops-spot-table :deep(thead th),
.ops-spot-table :deep(tbody td) {
  padding: 10px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  vertical-align: middle;
}

.ops-spot-table :deep(thead th) {
  background: rgba(0, 0, 0, 0.02);
  color: rgba(46, 46, 46, 0.85);
  font-weight: 500;
  white-space: nowrap;
}

.ops-spot-table :deep(tbody tr:hover td) {
  background: rgba(230, 243, 255, 0.2);
}

.ops-spot-table :deep(thead th.spot-col-time),
.ops-spot-table :deep(tbody td.spot-col-time) {
  position: sticky;
  left: 0;
  z-index: 2;
  box-shadow: 1px 0 0 rgba(0, 0, 0, 0.06);
}

.ops-spot-table :deep(thead th.spot-col-time) {
  z-index: 3;
}

.ops-spot-table :deep(tbody td.spot-col-time) {
  background: #fff;
}

.ops-spot-table :deep(tbody tr:hover td.spot-col-time) {
  background: rgba(230, 243, 255, 0.2);
}

.spot-chart {
  flex: 1;
  /* flex 子项 height:100% 在无明确父高时易为 0，图表不渲染；给固定高度保证 VChart 可见 */
  min-height: 280px;
  height: 280px;
  background: rgba(255, 255, 255, 0.65);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  overflow: hidden;
  padding: 6px 0 0;
  box-sizing: border-box;
}

.spot-chart-inner {
  width: 100%;
  min-height: 260px;
  height: 260px;
}

.spot-chart-empty {
  width: 100%;
  height: 260px;
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(46, 46, 46, 0.55);
  font-size: 13px;
}

.cell-number {
  width: 140px;
}

.cell-text {
  display: inline-block;
  min-width: 80px;
  text-align: right;
  color: rgba(46, 46, 46, 0.82);
}

.cell-text--center {
  text-align: center;
  min-width: 0;
}

.op-placeholder {
  color: rgba(46, 46, 46, 0.45);
}

.spot-empty-cell {
  text-align: center;
  padding: 32px 0;
  color: rgba(46, 46, 46, 0.45);
}

/* 按钮：统一使用 .meter-feature-btn（见 ../../_shared/feature-button.scss） */
</style>

